/**
 * 🔐 AUTHENTICATION MODULE - CRITICAL SYSTEM FILE
 * 
 * ⚠️  AI HELPER NOTICE: READ AI_HELPER_FIREBASE_AUTH_GUIDE.md BEFORE MODIFYING
 * ⚠️  This file handles form authentication using REAL Firebase SDK methods
 * ⚠️  DO NOT REPLACE with custom authentication without explicit permission
 * ⚠️  Uses authentic Firebase functions: signInWithEmailAndPassword, etc.
 * 
 * Key Features:
 * - Real Firebase authentication methods
 * - Form validation and error handling
 * - Production-identical behavior in emulator
 * - Proper user redirect logic
 */

// js/modules/auth.js

export function setupAuthFormToggling() {
    // Get form elements (these IDs come from your auth-forms.html partial)
    const loginFormEl = document.getElementById('loginForm');
    const registerFormEl = document.getElementById('registerForm');
    const forgotPasswordFormEl = document.getElementById('forgotPasswordForm');

    // Get link elements (these IDs also come from auth-forms.html)
    const showRegisterLink = document.getElementById('showRegister');         // In login form
    const showLoginLink = document.getElementById('showLogin');               // In register form
    const forgotPasswordLink = document.getElementById('forgotPasswordLink'); // In login form
    const showLoginFromForgotLink = document.getElementById('showLoginFromForgot'); // In forgot password form

    if (showRegisterLink && loginFormEl && registerFormEl && forgotPasswordFormEl) {
        showRegisterLink.onclick = e => {
            e.preventDefault();
            loginFormEl.classList.add('hidden');
            forgotPasswordFormEl.classList.add('hidden');
            registerFormEl.classList.remove('hidden');
        };
    }

    if (showLoginLink && loginFormEl && registerFormEl && forgotPasswordFormEl) {
        showLoginLink.onclick = e => {
            e.preventDefault();
            registerFormEl.classList.add('hidden');
            forgotPasswordFormEl.classList.add('hidden');
            loginFormEl.classList.remove('hidden');
        };
    }

    if (forgotPasswordLink && loginFormEl && registerFormEl && forgotPasswordFormEl) {
        forgotPasswordLink.onclick = e => {
            e.preventDefault();
            loginFormEl.classList.add('hidden');
            registerFormEl.classList.add('hidden');
            forgotPasswordFormEl.classList.remove('hidden');
        };
    }

    if (showLoginFromForgotLink && loginFormEl && registerFormEl && forgotPasswordFormEl) {
        showLoginFromForgotLink.onclick = e => {
            e.preventDefault();
            forgotPasswordFormEl.classList.add('hidden');
            registerFormEl.classList.add('hidden');
            loginFormEl.classList.remove('hidden');
        };
    }
}

export function initializeAuthFormEventListeners(
    authInstance,       // Firebase auth service instance
    usersRefInstance,   // Firebase Realtime DB reference to 'users'
    setDbFn,            // Firebase 'set' function for DB
    childDbFn,          // Firebase 'child' function for DB
    createUserFn,       // Firebase 'createUserWithEmailAndPassword' function
    signInFn,           // Firebase 'signInWithEmailAndPassword' function
    sendPasswordResetEmailFn, // Firebase 'sendPasswordResetEmail' function
    getDbFn             // Firebase 'get' function for DB (added for forgot password)
) {
    const loginFormEl = document.getElementById('loginForm');
    const registerFormEl = document.getElementById('registerForm');
    const forgotPasswordFormEl = document.getElementById('forgotPasswordForm');

    // Login Form Submission
    if (loginFormEl) {
        loginFormEl.onsubmit = e => {
            e.preventDefault();
            // Get input elements by ID from the loaded form
            const loginEmailEl = document.getElementById('loginEmail');
            const loginPasswordEl = document.getElementById('loginPassword');

            if (loginEmailEl && loginPasswordEl) {
                // Disable the submit button to prevent double-clicks
                const submitBtn = loginFormEl.querySelector('button[type="submit"]');
                if (submitBtn) {
                    submitBtn.disabled = true;
                    submitBtn.textContent = 'Logging in...';
                }

                // Set the logging in flag to ensure proper redirect
                if (typeof window.setLoggingInFlag === 'function') {
                    window.setLoggingInFlag();
                }

                const attemptLogin = async (retryCount = 0) => {
                    try {
                        const userCredential = await signInFn(authInstance, loginEmailEl.value, loginPasswordEl.value);
                        // Login successful
                        console.log('✅ Login successful for user:', userCredential.user.email);
                        
                        // Set the logging in flag for proper redirect
                        if (typeof window.setLoggingInFlag === 'function') {
                            console.log('🔄 Setting logging in flag');
                            window.setLoggingInFlag();
                        }
                        
                        if (submitBtn) {
                            submitBtn.textContent = 'Success! Redirecting...';
                        }
                        
                        // Direct redirect with shorter timeout for better UX
                        console.log('🚀 Initiating redirect to dashboard...');
                        setTimeout(() => {
                            console.log('🔄 Redirecting to dashboard.html');
                            window.location.href = 'dashboard.html';
                        }, 800);
                    } catch (err) {
                        console.error('Login error:', err);
                        
                        // Handle Firebase visibility check errors with retry
                        if (err.code === 'auth/visibility-check-was-unavailable' && retryCount < 3) {
                            console.log(`🔄 Retrying login attempt ${retryCount + 1}/3...`);
                            if (submitBtn) {
                                submitBtn.textContent = `Retrying... (${retryCount + 1}/3)`;
                            }
                            
                            // Track auth errors for fallback logic
                            const errorCount = parseInt(localStorage.getItem('firebase-auth-error-count') || '0') + 1;
                            localStorage.setItem('firebase-auth-error-count', errorCount.toString());
                            
                            // Wait with exponential backoff before retry
                            const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
                            setTimeout(() => attemptLogin(retryCount + 1), delay);
                            return;
                        }
                        
                        // Show user-friendly error message
                        let errorMessage = err.message;
                        if (err.code === 'auth/visibility-check-was-unavailable') {
                            // After retries failed, offer to switch to mixed mode
                            const switchToMixed = confirm(
                                '🚨 Firebase authentication is experiencing issues.\n\n' +
                                'Would you like to switch to Mixed Mode?\n' +
                                '(This uses production auth with local database)\n\n' +
                                'Click OK to switch automatically, or Cancel to retry later.'
                            );
                            
                            if (switchToMixed) {
                                console.log('🔄 Switching to mixed mode due to auth issues...');
                                localStorage.setItem('firebase-mixed-mode', 'true');
                                localStorage.setItem('firebase-mode', 'production');
                                if (submitBtn) {
                                    submitBtn.textContent = 'Switching modes...';
                                }
                                setTimeout(() => {
                                    window.location.reload();
                                }, 1000);
                                return;
                            }
                            
                            errorMessage = 'Firebase service temporarily unavailable. Please try again in a few moments.';
                        }
                        
                        alert(`Login failed: ${errorMessage}`);
                        // Re-enable the submit button
                        if (submitBtn) {
                            submitBtn.disabled = false;
                            submitBtn.textContent = 'Login';
                        }
                    }
                };
                
                attemptLogin();
            } else {
                alert("Login form inputs not found.");
            }
        };
    }

    // Register Form Submission
    if (registerFormEl) {
        registerFormEl.onsubmit = e => {
            e.preventDefault();
            const regFirstEl = document.getElementById('regFirst');
            const regLastEl = document.getElementById('regLast');
            const regEmailEl = document.getElementById('regEmail');
            const regPassEl = document.getElementById('regPass');

            const first = regFirstEl?.value.trim();
            const last = regLastEl?.value.trim();
            const email = regEmailEl?.value.trim();
            const pass = regPassEl?.value;

            if (!first || !last || !email || !pass || pass.length < 6) {
                alert("Please fill all fields (password min 6 chars).");
                return;
            }

            // Disable the submit button
            const submitBtn = registerFormEl.querySelector('button[type="submit"]');
            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.textContent = 'Creating account...';
            }

            createUserFn(authInstance, email, pass)
                .then(c => {
                    const userData = {
                        email: email,
                        firstName: first,
                        lastName: last,
                        role: 'Employee',
                        jobTitles: '',
                        locations: '',
                        status: 'pending',
                        hireDate: '',
                        birthday: '',
                        registeredAt: new Date().toISOString(),
                        tutorialSettings: {
                            enabled: true,
                            completed: false,
                            dismissed: false,
                            createdAt: new Date().toISOString()
                        }
                    };
                    
                    return setDbFn(childDbFn(usersRefInstance, c.user.uid), userData);
                })
                .then(() => {
                    alert('Registration submitted! Your account is pending admin approval. You will be notified once approved.');
                    const showLoginLink = document.getElementById('showLogin');
                    showLoginLink?.click(); // Simulate click to switch to login form
                    registerFormEl.reset();
                    // Re-enable the submit button
                    if (submitBtn) {
                        submitBtn.disabled = false;
                        submitBtn.textContent = 'Create Account';
                    }
                })
                .catch(err => {
                    alert(`Registration failed: ${err.message}`);
                    // Re-enable the submit button
                    if (submitBtn) {
                        submitBtn.disabled = false;
                        submitBtn.textContent = 'Create Account';
                    }
                });
        };
    }

    // Forgot Password Form Submission
    if (forgotPasswordFormEl) {
        forgotPasswordFormEl.onsubmit = async e => {
            e.preventDefault();
            const forgotEmailEl = document.getElementById('forgotEmail');
            const forgotFirstNameEl = document.getElementById('forgotFirstName');
            const forgotLastNameEl = document.getElementById('forgotLastName');

            const email = forgotEmailEl?.value.trim();
            const firstName = forgotFirstNameEl?.value.trim();
            const lastName = forgotLastNameEl?.value.trim();

            if (!email || !firstName || !lastName) {
                alert("Please fill in all fields: Email, First Name, and Last Name.");
                return;
            }

            // Disable the submit button
            const submitBtn = forgotPasswordFormEl.querySelector('button[type="submit"]');
            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.textContent = 'Sending...';
            }

            try {
                const usersSnapshot = await getDbFn(usersRefInstance);
                const allUsersForCheck = usersSnapshot.val() || {};
                
                let userFound = false;
                let matchedUserEmail = null;

                for (const uid in allUsersForCheck) {
                    const user = allUsersForCheck[uid];
                    if (user.email === email &&
                        user.firstName?.toLowerCase() === firstName.toLowerCase() &&
                        user.lastName?.toLowerCase() === lastName.toLowerCase()) {
                        userFound = true;
                        matchedUserEmail = user.email;
                        break;
                    }
                }

                if (userFound && matchedUserEmail) {
                    await sendPasswordResetEmailFn(authInstance, matchedUserEmail);
                    alert(`If your details are correct and an account exists for ${matchedUserEmail}, a password reset email has been sent. Please check your inbox and spam folder.`);
                    forgotPasswordFormEl.reset();
                } else {
                    alert("The information provided does not match an active account, or there was an issue. If you believe this is an error, please contact support.");
                    forgotPasswordFormEl.reset();
                }
            } catch (error) {
                console.error("Password reset error:", error);
                alert(`Error sending password reset email: ${error.message}. Please try again or contact support.`);
                forgotPasswordFormEl.reset();
            } finally {
                // Re-enable the submit button
                if (submitBtn) {
                    submitBtn.disabled = false;
                    submitBtn.textContent = 'Send Reset Email';
                }
            }
        };
    }
}
